import React, { useState, useEffect, useRef } from 'react';
import { trackEvent } from '../utils/bridge';
import { captureAttribution } from '../utils/attribution';
import townCatalog from './assessmentTowns.json';
import halisiQuestions from './assessmentHalisiQuestions.json';


const gold = '#f6cf64';
// Assessment-only embedding avoids deployment-specific logo routing and the shared
// site-theme URL rewrite. Other pages keep their existing logo pipeline.
const upendoAssessmentLogo = typeof __ASSESSMENT_UPENDO_LOGO__ === 'string'
    ? __ASSESSMENT_UPENDO_LOGO__ : '/assets/img/uhlogo.jpeg';
export function getAssessmentTheme(hostname = '') {
    const host = hostname.toLowerCase();
    const matches = domain => host === domain || host.endsWith(`.${domain}`);
    if (matches('upendo-halisi.com')) return { title: 'Upendo Halisi', upendo: true, logo: upendoAssessmentLogo };
    if (matches('expatelitesingles.com')) return { title: 'Expat Elite Singles' };
    return { title: 'Penzi Halisi' };
}
const postPitchChoicesArtwork = typeof __ASSESSMENT_CHOICE_ART__ === 'string'
    ? __ASSESSMENT_CHOICE_ART__ : '/assets/img/assessment/post-pitch-choices.webp';
const upendoPostPitchChoicesArtwork = typeof __ASSESSMENT_UPENDO_CHOICE_ART__ === 'string'
    ? __ASSESSMENT_UPENDO_CHOICE_ART__ : '/assets/img/assessment/upendo-post-pitch-choices.webp';

export function getAssessmentPhoneCountry(hostname = '') {
    return getAssessmentTheme(hostname).upendo ? '+255' : '+254';
}

const introCopy = {
    landingTitle: 'The Structured Approach to Finding Your Equal.',
    landingDescription: 'A private, high-discretion platform designed for professionals seeking intentional, long-term commitment.',
    eyebrow: 'Private Match Discovery: Designed to Connect Remarkable Women with Purpose-Driven Men.',
    title: 'Your next chapter Starts here',

};

const backgroundImage = '/assets/img/assessment/assessment-bg.webp';
const landingBackgroundImage = '/assets/img/assessment/assessment-landing-couple.webp';
const qualifiedPosterImage = '/assets/img/assessment/qualified-poster.webp?v=2';
const disqualifiedPosterImage = '/assets/img/assessment/disqualified-poster.webp?v=2';
// Keep the original artwork for other brands and non-private entrypoints.
export function resolveAssessmentPosterImage(posterSrc, hostname, privateFlow) {
    const host = String(hostname || '').toLowerCase();
    if (!privateFlow || !(host === 'upendo-halisi.com' || host.endsWith('.upendo-halisi.com'))) return posterSrc;
    if (posterSrc === qualifiedPosterImage) return '/assets/img/assessment/upendo-qualified-poster.webp?v=1';
    if (posterSrc === disqualifiedPosterImage) return '/assets/img/assessment/upendo-disqualified-poster.webp?v=1';
    return posterSrc;
}
// Native MP4 for https://player.mediadelivery.net/play/630821/d98eb4d2-55f0-4dc5-bcab-746f96bbcfc8.
const qualifiedVideoUrl = 'https://vz-31c13e5e-39b.b-cdn.net/d98eb4d2-55f0-4dc5-bcab-746f96bbcfc8/play_720p.mp4';
// Native MP4 verified from https://player.mediadelivery.net/play/612846/504373a2-fbb1-43db-b670-45a2d36d462b.
export function getAssessmentQualifiedVideoUrl(hostname) {
    const host = String(hostname || '').toLowerCase();
    return host === 'upendo-halisi.com' || host.endsWith('.upendo-halisi.com')
        ? 'https://vz-bf43f0c6-6c3.b-cdn.net/504373a2-fbb1-43db-b670-45a2d36d462b/play_720p.mp4'
        : qualifiedVideoUrl;
}

const testimonialVideoIds = [
    '3b383de0-6761-40c6-9f43-f8f58279276f',
    '668fe06b-7eec-48ec-b069-96c3ca5d13d4',
    'aa8780b4-5d3d-4d3e-992c-d469178a3e66',
    '11d7cd80-4e14-4217-9dc8-b684bd8bfc8f',
    'fc8802b7-7539-43eb-b3d1-5351d643ef39',
];

// Featured bachelors first; omit their duplicate uploads from the homepage pool.
const bachelorVideoIds = [
    'b3c1f633-612b-4b7e-99fd-29270b6bbbc2',
    '00862856-1914-4c41-8bda-7cd758d85e17',
    'feefd5dd-52bc-4f9d-aa22-48dd7a00835b',
    '3136ad62-2771-4ac2-b58c-140613b0ad61',
    '49309921-ea82-40ac-a7fb-850f57c50b2a',
    '1f7a6cb0-565d-47c3-a8bc-0761b6c65d3f',
    '8ecb8a36-6e3f-4ee5-8285-1cc373a4d856',
    'b5afe174-17ed-4555-8e07-b37383705e05',
    'd9efcd3e-1a65-4400-9c26-e13fd9e2140d',
];

export function isAssessmentPhoneValid(phone) {
    return /^[0-9]{9,10}$/.test(String(phone || ''));
}

export const phoneCountries = [
    { name: 'Kenya', code: '+254' },
    { name: 'Uganda', code: '+256' },
    { name: 'Tanzania', code: '+255' },
    { name: 'Rwanda', code: '+250' },
    { name: 'Burundi', code: '+257' },
    { name: 'South Sudan', code: '+211' },
    { name: 'Somalia', code: '+252' },
    { name: 'Ethiopia', code: '+251' },
    { name: 'Eritrea', code: '+291' },
    { name: 'Djibouti', code: '+253' },
];

export function formatAssessmentPhone(phone, countryCode = '+254') {
    const raw = String(phone || '').trim();
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    if (raw.startsWith('+')) return `+${digits}`;
    if (raw.startsWith('00')) return `+${digits.slice(2)}`;
    const prefix = countryCode.slice(1);
    if (digits.startsWith(prefix)) return `+${digits}`;
    return `${countryCode}${digits.replace(/^0+/, '')}`;
}

// Store the complete query, including duplicate keys, without creating arbitrary cookies.
// A new nonempty query replaces the previous visit; query-free visits retain it.
export function rememberAssessmentQuery() {
    try {
        const query = window.location.search.replace(/^\?/, '');
        if (!query) return;
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `halaallove_assessment_params=${encodeURIComponent(query)}; Max-Age=7776000; Path=/; SameSite=Lax${secure}`;
    } catch (_) {
        // Cookie restrictions must never interrupt the assessment.
    }
}

export function rememberAssessmentGender(gender) {
    try {
        // Share the exact domain-scoped key read by the static index page.
        const key = `halisi-gender-gate-v1:${window.location.hostname}`;
        if (gender === 'Female') window.localStorage.setItem(key, 'complete');
        else if (gender === 'Male') window.localStorage.removeItem(key);
    } catch (_) {
        // Storage restrictions must not interrupt the assessment.
    }
}

const gateSteps = [
    {
        key: 'gender',
        stepLabel: 'Step 1 of 3',
        title: <>Please select your <span>gender:</span></>,
        options: [
            { value: 'Female', label: 'Female', icon: '♀' },
            { value: 'Male', label: 'Male', icon: '♂', disqualify: true },
        ],
    },
    {
        key: 'age',
        stepLabel: 'Step 2 of 3',
        title: <>Which life <span>stage / age group</span> best describes you currently?</>,
        options: [
            { value: 'Under 26', label: 'Under 26', badge: '<26', disqualify: true },
            { value: '26-30', label: '26–30', badge: '26' },
            { value: '31-35', label: '31–35', badge: '31' },
            { value: '36-40', label: '36–40', badge: '36' },
            { value: '41-45', label: '41–45', badge: '41' },
            { value: '46-50', label: '46–50', badge: '46' },
            { value: '51-55', label: '51–55', badge: '51' },
            { value: '56-60', label: '56–60', badge: '56' },
            { value: '60+', label: '60+', badge: '60+' },

        ],
    },
    {
        key: 'location',
        stepLabel: 'Step 3 of 3',
        title: <>Which town are you currently based in?</>,
        options: [],
    },
];

const privateStartStep = gateSteps.length + 1;

export const privateQuestions = [
    {
        label: 'Personal well-being and lifestyle',
        key: 'lifestyle_spend',
        title: 'When you invest in your personal well-being and lifestyle (such as boutique fitness, wellness retreats, professional executive coaching, or premium personal grooming), what does your typical monthly spend look like?',
        options: [
            { value: 'Under KES 5,000 / month', label: 'Under KES 5,000 / month', disqualify: true },
            { value: 'KES 5,000 – KES 15,000 / month', label: 'KES 5,000 – KES 15,000 / month' },
            { value: 'KES 15,000 – KES 40,000 / month', label: 'KES 15,000 – KES 40,000 / month' },
            { value: 'Over KES 40,000 / month', label: 'Over KES 40,000 / month' },
        ],
    },
    {
        label: 'Preferred meeting environment',
        key: 'meeting_environment',
        title: 'To help us introduce you to someone whose daily rhythm and lifestyle match yours, where do you naturally feel most comfortable meeting for an evening dinner or weekend connection?',
        options: [
            { value: 'Casual local coffee shops, neighbourhood eateries, or quiet informal spots', label: 'Casual local coffee shops, neighbourhood eateries, or quiet informal spots', disqualify: true },
            { value: "Upscale boutique bistros, fine dining establishments, or private members' spaces", label: "Upscale boutique bistros, fine dining establishments, or private members' spaces" },
            { value: 'Outdoor experiential spaces, country clubs, or weekend travel retreats', label: 'Outdoor experiential spaces, country clubs, or weekend travel retreats' },
        ],
    },
    {
        label: 'Current search and intent',
        key: 'search_intent',
        title: 'Professional matchmaking requires intentional time, vetted introductions, and privacy protection. How would you describe your current search?',
        options: [
            { value: 'I am just curious and exploring what is out there right now.', label: 'I am just curious and exploring what is out there right now.', disqualify: true },
            { value: 'I am open to dating, but prefer free apps where I can browse on my own time.', label: 'I am open to dating, but prefer free apps where I can browse on my own time.', disqualify: true },
            { value: 'My time and privacy are valuable. I am actively seeking an intentional, committed partnership and want a curated, professional service.', label: 'My time and privacy are valuable. I am actively seeking an intentional, committed partnership and want a curated, professional service.' },
        ],
    },
    {
        label: 'Financial Question',
        key: 'q6b_financial',
        title: `Our network is private and strictly vetted. To maintain this standard, we offer 5 distinct tiers depending on the level of private vetting and guidance you need. Lowest package price is Kshs. 4,400 and highest is Kshs. 35,000, we have 5 packages. If you qualify, are you financially prepared and ready to invest within this range to find your future soulmate/husband?`,
        options: [
            { value: 'Yes, absolutely — I understand this is an investment in my future, and I am ready to get started.', label: 'Absolutely, I understand this is an investment in my future, and I\'m ready to get started.' },
            { value: 'Yes, depending on the tier — I can start within the lower tier once I see what fits me best.', label: 'Depending on the tier, I can start within the lower tier once I see what fits me best.' },
            { value: 'Yes, at the highest tier — I want full concierge vetting and the top-tier service.', label: 'Yes, at the highest tier, I want full concierge vetting and the top-tier service.' },
            { value: 'No — I am not in a position to invest financially at this time.', label: "I'm not in a position to invest financially at this time.", disqualify: true },
        ],
    },
]

function optionValue(option) {
    return typeof option === 'string' ? option : option.value;
}

function optionLabel(option) {
    return typeof option === 'string' ? option : option.label;
}

const AssessmentFunnel = ({ privateFlow = false } = {}) => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    // Initialise once, not in a hostname/step effect: manual changes survive Back.
    const [contact, setContact] = useState(() => ({ first_name: '', last_name: '', phone: '', email: '', countryCode: getAssessmentPhoneCountry(typeof window !== 'undefined' ? window.location.hostname : '') }));
    const [hostname, setHostname] = useState('');
    const [exitMessage, setExitMessage] = useState('');
    const [showQualifiedVideo, setShowQualifiedVideo] = useState(false);
    const [showContinue, setShowContinue] = useState(false);
    const [showPlayPrompt, setShowPlayPrompt] = useState(false);
    const [testimonialIndex, setTestimonialIndex] = useState(null);
    const [showBachelors, setShowBachelors] = useState(false);
    const pitchVideoRef = useRef(null);
    const trackedPixelEvents = useRef(new Set());
    const lastBridgeStage = useRef('');
    const trackedVideoEvents = useRef(new Set());
    const leadId = useRef(null);
    const submittedStatuses = useRef(new Set());
    const funnelRef = useRef(null);
    const currentHost = String(hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();
    const halisiDesign = ['penzi-halisi.com', 'upendo-halisi.com'].some(domain => currentHost === domain || currentHost.endsWith(`.${domain}`));
    const consolidatedFlow = privateFlow && halisiDesign;
    const flowQuestions = consolidatedFlow ? halisiQuestions : privateQuestions;

    const trackPrivateEvent = (eventName, details = {}) => {
        if (!privateFlow) return;
        try {
            // Reuse domain routing, anonymous visitor/session IDs and digest storage.
            // Do not put contact details or private answer text in beacon URLs.
            // Suppress the shared bridge's logged-in email/actor fallback too.
            trackEvent(`assessment_private_${eventName}`, { flow: 'assessment_private', ...details, user: '' });
        } catch (_) {
            // Analytics failures must never interrupt the questionnaire.
        }
    };

    useEffect(() => {
        let stage = 'contact';
        if (step === -1) stage = 'disqualified';
        else if (step === 99) stage = showContinue ? 'continue' : showQualifiedVideo ? 'video' : 'qualified';
        else if (step >= 1 && step <= gateSteps.length) stage = gateSteps[step - 1].key;
        else if (step >= privateStartStep) stage = flowQuestions[step - privateStartStep]?.key;
        if (!stage || lastBridgeStage.current === stage) return;
        lastBridgeStage.current = stage;
        if (consolidatedFlow && step === 1) {
            gateSteps.forEach(gate => trackPrivateEvent(`${gate.key}_viewed`));
            return;
        }
        trackPrivateEvent(`${stage}_viewed`);
    }, [privateFlow, step, showQualifiedVideo, showContinue, consolidatedFlow]);

    const trackVideoEvent = (eventName) => {
        if (trackedVideoEvents.current.has(eventName)) return;
        trackedVideoEvents.current.add(eventName);
        trackPrivateEvent(eventName);
    };

    const goBack = (targetStep) => {
        trackPrivateEvent('back_clicked', { from_step: String(step), to_step: String(targetStep) });
        setStep(targetStep);
    };

    const trackAssessmentEvent = (eventName) => {
        try {
            if (typeof window.fbq !== 'function' || trackedPixelEvents.current.has(eventName)) return;
            window.fbq('track', eventName);
            trackedPixelEvents.current.add(eventName);
        } catch (_) {
            // Tracking must never block assessment progression.
        }
    };

    const beginAssessment = () => {
        if (!isContactComplete) return;
        trackAssessmentEvent('Lead');
        trackPrivateEvent('started');
        submitLead({}, 'started');
        setStep(1);
    };

    useEffect(() => {
        rememberAssessmentQuery();
        captureAttribution();
        setHostname(window.location.hostname);
    }, []);

    useEffect(() => {
        if (step !== 99) {
            return undefined;
        }
        setShowQualifiedVideo(false);
        setShowContinue(false);
        setTestimonialIndex(null);
        setShowBachelors(false);
        const timer = window.setTimeout(() => setShowQualifiedVideo(true), 10000);
        return () => window.clearTimeout(timer);
    }, [step]);

    useEffect(() => {
        if (!showQualifiedVideo) {
            return;
        }
        document.querySelector('.qualified-video-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [showQualifiedVideo]);

    const playPitchVideo = () => {
        const video = pitchVideoRef.current;
        if (!video) return;
        video.muted = false;
        video.volume = 1;
        const attempt = video.play();
        if (attempt) attempt.catch(() => setShowPlayPrompt(true));
    };

    useEffect(() => {
        if (!showQualifiedVideo || showContinue) return undefined;
        playPitchVideo();
        return undefined;
    }, [showQualifiedVideo, showContinue]);

    useEffect(() => {
        // Long mobile answers can scroll both the page and its clipped card.
        // Always start the next question at its prompt, including after Back.
        const root = funnelRef.current;
        if (!root) return;
        [root, ...root.querySelectorAll('.assessment-phone')].forEach(element => {
            element.scrollTop = 0;
            element.scrollLeft = 0;
        });
    }, [step]);

    const theme = getAssessmentTheme(hostname || (typeof window !== 'undefined' ? window.location?.hostname : '') || '');
    const isPhoneValid = isAssessmentPhoneValid(contact.phone);
    const isContactComplete = (contact.first_name || '').trim() && (contact.last_name || '').trim() && isPhoneValid && contact.email.trim();

    const selectAnswer = (question, option, nextStep = step + 1) => {
        // Do not let a stale/back-edited set of gates qualify via the last page.
        if (consolidatedFlow && !isAboutYouEligible) {
            setStep(1);
            return;
        }
        const value = optionValue(option);
        const disqualify = typeof option !== 'string' && option.disqualify;
        const updatedAnswers = {
            ...answers,
            [question]: value,
        };
        if (consolidatedFlow) {
            const questionIndex = flowQuestions.findIndex(item => item.key === question);
            const invalidPrefix = flowQuestions.slice(0, questionIndex).findIndex(item => !item.options.some(choice => choice.value === answers[item.key] && !choice.disqualify));
            if (questionIndex < 0 || !flowQuestions[questionIndex].options.some(choice => choice.value === value)) return;
            if (invalidPrefix >= 0) { setStep(privateStartStep + invalidPrefix); return; }
            if (answers[question] !== value) flowQuestions.slice(questionIndex + 1).forEach(item => delete updatedAnswers[item.key]);
        }
        setAnswers(updatedAnswers);
        trackPrivateEvent('answer_selected', { question, outcome: disqualify ? 'disqualified' : 'passed' });
        if (question === 'gender') rememberAssessmentGender(value);
        if (disqualify) {
            submitLead(updatedAnswers, 'disqualified');
            setExitMessage('Thank you for taking the assessment. Based on your answer, this private matching path may not be the right fit at this stage.');
            setStep(-1);
            return;
        }
        if (question === flowQuestions[flowQuestions.length - 1].key) {
            submitLead(updatedAnswers);
            return;
        }
        setStep(nextStep);
    };

    const submitLead = async (answersForSubmit = answers, status = 'qualified') => {
        if (submittedStatuses.current.has(status)) return;
        submittedStatuses.current.add(status);
        if (!leadId.current) leadId.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        const payload = {
            lead_id: leadId.current,
            status,
            answers: answersForSubmit,
            contact: {
                ...contact,
                first_name: (contact.first_name || '').trim(),
                last_name: (contact.last_name || '').trim(),
                name: [contact.first_name, contact.last_name].map(value => (value || '').trim()).filter(Boolean).join(' '),
                phone: formatAssessmentPhone(contact.phone, contact.countryCode || '+254'),
            },
            tracking: { ...captureAttribution(), url: window.location.href, flow: 'assessment_private', schema_version: consolidatedFlow ? 4 : 2 },
        };
        const submissionStage = status === 'started' ? 'contact' : status;
        const trackSubmission = (result, details = {}) => trackPrivateEvent(
            `${submissionStage}_submission_${result}`, { submission_status: status, ...details }
        );
        trackSubmission('started');
        if (status === 'qualified') setStep(99);
        try {
            const response = await fetch('/api/submit_assessment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true,
            });
            trackSubmission(response.ok ? 'succeeded' : 'failed', { http_status: String(response.status) });
            if (!response.ok) submittedStatuses.current.delete(status);
        } catch (e) {
            submittedStatuses.current.delete(status);
            trackSubmission('failed', { reason: 'network_error' });
            console.error('Submission failed', e);
        }
    };

    const aboutYouOptions = [gateSteps[0], gateSteps[1]];
    const isAboutYouComplete = aboutYouOptions.every(gate => gate.options.some(option => option.value === answers[gate.key])) && Boolean(answers.location?.trim());
    const isAboutYouEligible = isAboutYouComplete && aboutYouOptions.every(gate => !gate.options.find(option => option.value === answers[gate.key])?.disqualify);
    const continueAboutYou = (event) => {
        event.preventDefault();
        if (!isAboutYouComplete) return;
        const updatedAnswers = { ...answers, location: answers.location.trim() };
        setAnswers(updatedAnswers);
        rememberAssessmentGender(updatedAnswers.gender);
        gateSteps.forEach(gate => {
            const rejected = gate.options.some(option => option.value === updatedAnswers[gate.key] && option.disqualify);
            trackPrivateEvent('answer_selected', { question: gate.key, outcome: rejected ? 'disqualified' : 'passed' });
        });
        if (!isAboutYouEligible) {
            submitLead(updatedAnswers, 'disqualified');
            setExitMessage('Thank you for taking the assessment. Based on your answer, this private matching path may not be the right fit at this stage.');
            setStep(-1);
            return;
        }
        setStep(privateStartStep);
    };

    const renderStageOutline = (active) => (
        <nav className="assessment-stages" aria-label="Assessment stages">
            <p className="step-label">Step {active} of 3 — {['BioData', 'About You', 'Package Readiness'][active - 1]}</p>
            <ol>
                {['BioData', 'About You', 'Package Readiness'].map((label, index) => (
                    <li key={label} aria-current={active === index + 1 ? 'step' : undefined} className={index + 1 <= active ? 'is-active' : ''}>
                        <span>{index + 1}</span> {label}
                    </li>
                ))}
            </ol>
        </nav>
    );

    const renderProgress = (active) => (
        <div className="assessment-progress" aria-label={`${active} of ${gateSteps.length} onboarding steps`}>
            {gateSteps.map((gate, index) => index + 1).map((item) => (
                <span key={item} className={item <= active ? 'is-active' : ''} />
            ))}
        </div>
    );

    const renderDecor = () => (
        <>
            <div className="gold-ribbon top" />
            <div className="gold-ribbon bottom" />
            <div className="gold-orb one" />
            <div className="gold-orb two" />
        </>
    );


    const renderShell = (children, extraClass = '', backgroundIndex = 0) => (
        <div className="assessment-stage">
            <div
                className={`assessment-phone ${extraClass}`}
                style={{ '--assessment-bg': `url(${privateFlow ? backgroundImage : landingBackgroundImage})` }}
            >
                {renderDecor()}
                {children}
            </div>
        </div>
    );

    const renderLanding = () => halisiDesign ? renderShell(
        <div className="atelier-landing">
            <div className="atelier-landing-copy">
                <p className="atelier-eyebrow">PRIVATE MATCH DISCOVERY</p>
                <h1>The structured approach to <em>finding your equal.</em></h1>
                <p className="atelier-description">{introCopy.landingDescription}</p>
                <div className="atelier-journey"><span>01 · BioData</span><span>02 · About You</span><span>03 · Package Readiness</span></div>
                <a className="gold-cta" href={`/assessment/private${typeof window !== 'undefined' ? window.location.search || '' : ''}`}>Continue <span aria-hidden="true">→</span></a>
                <p className="atelier-note">Intentional connections. A considered first step towards your next chapter.</p>
            </div>
        </div>, 'landing-phone atelier-panel'
    ) : renderShell(
        <div className="closer-screen bridge-screen landing-screen">
            <div className="bridge-copy">
                <div className="gold-divider centered" />
                <h1>{introCopy.landingTitle}</h1>
                <p>{introCopy.landingDescription}</p>
            </div>
            <a className="gold-cta" href={`/assessment/private${typeof window !== 'undefined' ? window.location.search || '' : ''}`}>
                Continue
            </a>
        </div>,
        'landing-phone'
    );

    const renderIntro = () => renderShell(
        <div className="intro-screen">
            {consolidatedFlow && renderStageOutline(1)}
            <div className="intro-copy">
                <h1>{consolidatedFlow ? <>Your next chapter <em>starts here.</em></> : introCopy.title}</h1>
                <h2>{consolidatedFlow ? 'Begin with your BioData.' : introCopy.eyebrow}</h2>
                {consolidatedFlow && <p className="atelier-description">A few details to begin your private match discovery.</p>}
                <div className="gold-divider" />

                <div className="contact-fields intro-contact-fields mb-4">
                    {consolidatedFlow && <label htmlFor="assessment-first-name">First name</label>}
                    <input id="assessment-first-name" type="text" name="first_name" aria-label="First name" autoComplete="given-name" placeholder="First name" value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} />
                    {consolidatedFlow && <label htmlFor="assessment-last-name">Last name</label>}
                    <input id="assessment-last-name" type="text" name="last_name" aria-label="Last name" autoComplete="family-name" placeholder="Last name" value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} />
                    {consolidatedFlow && <label htmlFor="assessment-contact-phone">WhatsApp number</label>}
                    <div className="assessment-phone-input">
                        <select aria-label="Phone country code" value={contact.countryCode || '+254'} onChange={(e) => { setContact({ ...contact, countryCode: e.target.value }); setAnswers(previous => { const next = { ...previous }; delete next.location; return next; }); }}>
                            {phoneCountries.map((country) => (
                                <option key={country.code} value={country.code}>{country.name} ({country.code})</option>
                            ))}
                        </select>
                        <input id="assessment-contact-phone" type="tel" inputMode="numeric" minLength={9} maxLength={10} pattern="[0-9]{9,10}" aria-invalid={Boolean(contact.phone) && !isPhoneValid} aria-describedby="assessment-phone-help" autoComplete="tel-national" aria-label="WhatsApp Number" placeholder="WhatsApp Number" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                    </div>
                    <p id="assessment-phone-help" className="phone-help" role={contact.phone && !isPhoneValid ? 'alert' : undefined}>Enter 9–10 digits, without the country code.</p>
                    {consolidatedFlow && <label htmlFor="assessment-contact-email">Email address</label>}
                        <input id="assessment-contact-email" type="email" autoComplete="email" placeholder="Your Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </div>
                <button type="button" className="gold-cta" disabled={!isContactComplete} onClick={beginAssessment}>
                    Let&apos;s Begin <span>→</span>
                </button>
            </div>
        </div>,
        'intro-phone',
        0
    );

    const renderAboutYou = () => {
        const country = phoneCountries.find(item => item.code === contact.countryCode) || phoneCountries[0];
        const town = answers.location || '';
        const towns = (townCatalog?.[country.code] || []).filter(item => item.toLocaleLowerCase().includes(town.trim().toLocaleLowerCase()));
        return renderShell(
            <div className="question-screen about-you-screen">
                <button type="button" className="back-button" aria-label="Back to BioData" onClick={() => goBack(0)}>‹</button>
                {renderStageOutline(2)}
                <h1 className="question-title smaller">About You</h1>
                <p className="atelier-description">A little context for a more intentional introduction.</p>
                <form className="town-picker about-you-fields" onSubmit={continueAboutYou}>
                    <label htmlFor="assessment-gender">Gender</label>
                    <select id="assessment-gender" required value={answers.gender || ''} onChange={event => setAnswers({ ...answers, gender: event.target.value })}>
                        <option value="" disabled>Select your gender</option>
                        {gateSteps[0].options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <label htmlFor="assessment-town">Your town in {country.name}</label>
                    <input id="assessment-town" type="text" required list="assessment-town-options" autoComplete="off" placeholder="Start typing your town" value={town} onChange={event => setAnswers({ ...answers, location: event.target.value })} />
                    <datalist id="assessment-town-options">
                        {towns.map(item => <option key={item} value={item} />)}
                        <option value="Diaspora / International" />
                    </datalist>
                    <label htmlFor="assessment-age">Age group</label>
                    <select id="assessment-age" required value={answers.age || ''} onChange={event => setAnswers({ ...answers, age: event.target.value })}>
                        <option value="" disabled>Select your age group</option>
                        {gateSteps[1].options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <button type="submit" className="gold-cta" disabled={!isAboutYouComplete}>Continue <span>→</span></button>
                </form>
            </div>
        );
    };

    const renderGateStep = (gateIndex) => {
        const config = gateSteps[gateIndex];
        const options = config.options;
        const country = phoneCountries.find(item => item.code === contact.countryCode) || phoneCountries[0];
        const towns = townCatalog?.[country.code] || [];
        const selectedTown = answers.location || '';
        const matchingTowns = towns.filter(town => town.toLocaleLowerCase().includes(selectedTown.trim().toLocaleLowerCase()));
        return renderShell(
            <div className={`question-screen gate-screen gate-${config.key}`}>
                <button type="button" className="back-button" onClick={() => goBack(Math.max(0, step - 1))}>‹</button>
                <p className="step-label">{config.stepLabel}</p>
                {renderProgress(gateIndex + 1)}
                <div className="gate-content">
                <h1 className="question-title">{config.title}</h1>
                {config.key === 'location' ? (
                    <div className="town-picker">
                        <label htmlFor="assessment-town">Your town in {country.name}</label>
                        <input id="assessment-town" type="text" list="assessment-town-options" autoComplete="off" placeholder="Start typing your town" value={selectedTown} onChange={event => setAnswers({ ...answers, location: event.target.value })} onKeyDown={event => {
                            if (event.key === 'Enter' && selectedTown.trim()) { event.preventDefault(); selectAnswer(config.key, selectedTown.trim()); }
                        }} />
                        <datalist id="assessment-town-options">
                            {matchingTowns.map(town => <option key={town} value={town} />)}
                            <option value="Diaspora / International" />
                        </datalist>
                        <button type="button" className="gold-cta" disabled={!selectedTown.trim()} onClick={() => { if (selectedTown.trim()) selectAnswer(config.key, selectedTown.trim()); }}>Continue <span>→</span></button>
                    </div>
                ) : <div className="option-list">
                    {options.map((option) => {
                        const value = optionValue(option);
                        const selected = answers[config.key] === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                className={`lux-option ${selected ? 'selected' : ''}`}
                                onClick={() => selectAnswer(config.key, option)}
                            >
                                <span className="option-medallion">{option.badge || option.icon || '✓'}</span>
                                <span class="text-xs">{optionLabel(option)}</span>
                                <b>›</b>
                            </button>
                        );
                    })}
                </div>}
                </div>
            </div>,
            '',
            gateIndex
        );
    };

    const renderQuestionTitle = (title) => title.split(/\n+/).filter((line) => line.trim()).map((line, index) => (
        <span className="financial-paragraph text-xs" key={`${index}-${line}`}>
            {line}
        </span>
    ));

    const renderPrivateQuestion = (questionIndex) => {
        const question = flowQuestions[questionIndex];
        const host = currentHost;
        const isUpendo = host === 'upendo-halisi.com' || host.endsWith('.upendo-halisi.com');
        const isPenzi = host === 'penzi-halisi.com' || host.endsWith('.penzi-halisi.com');
        const isFinancial = question.key === 'q6b_financial' || question.key === 'financial_readiness';
        const isTanzaniaFinancial = isFinancial && (isUpendo || (!isPenzi && contact.countryCode === '+255'));
        // Named brands have fixed price ranges, independent of the phone country.
        const priceRange = isUpendo ? 'Tshs. 137,500 to Tshs. 850,000' : 'Kshs. 5,500 to Kshs. 35,000';
        let title = question.title.replace('{brand}', theme.title).replace('{range}', priceRange);
        if (isFinancial && (isUpendo || isPenzi)) {
            const priceRange = isUpendo ? 'Tshs. 137,500 to Tshs. 850,000' : 'Kshs. 4,400 to Kshs. 35,000';
            title = title.replace('Lowest package price is Kshs. 4,400 and highest is Kshs. 35,000, we have 5 packages.', `Our package prices range from ${priceRange}; we have 5 packages.`);
        } else if (isTanzaniaFinancial) {
            title = title.replace('Kshs. 4,400', 'TSh 110,000').replace('Kshs. 35,000', 'TSh 875,000');
        }
        return renderShell(
            <div className="question-screen private-screen">
                <button type="button" className="back-button" aria-label="Back" onClick={() => goBack(consolidatedFlow && questionIndex === 0 ? 1 : Math.max(gateSteps.length, step - 1))}>‹</button>

                {consolidatedFlow && renderStageOutline(3)}
                <p className="step-label" aria-live="polite">Question {questionIndex + 1} of {flowQuestions.length}</p>
                <h1 className="question-title smaller">{renderQuestionTitle(title)}</h1>
                {question.subtitle && <p className="question-subtitle">{question.subtitle}</p>}
                <div className="option-list compact">
                    {question.options.map((option, optionIndex) => {
                        const value = optionValue(option);
                        return (
                            <button
                                key={value}
                                type="button"
                                className="lux-option text-option"
                                aria-pressed={consolidatedFlow ? answers[question.key] === value : undefined}
                                onClick={() => selectAnswer(question.key, option)}
                            >
                                <span className="option-medallion small">{consolidatedFlow ? String.fromCharCode(65 + optionIndex) : question.key === 'q6b_financial' ? (isTanzaniaFinancial ? 'TSh' : 'KES') : String.fromCharCode(65 + optionIndex)}</span>
                                <span>{optionLabel(option)}</span>
                                <b>›</b>
                            </button>
                        );
                    })}
                </div>
            </div>,
            'private-phone',
            questionIndex
        );
    };

    const renderPosterScreen = (posterSrc, altText) => {
        const themedPosterSrc = resolveAssessmentPosterImage(posterSrc, hostname, privateFlow);
        return (
            <div className={themedPosterSrc !== posterSrc ? 'result-poster-screen upendo-result-poster-screen' : 'result-poster-screen'}>
                <img className="result-poster" src={themedPosterSrc} alt={altText} />
            </div>
        );
    };

    const renderIneligible = () => renderPosterScreen(
        disqualifiedPosterImage,
        exitMessage || "Thank you. Unfortunately you don't qualify to use this service."
    );

    const renderQualified = () => {
        if (showContinue) {
            if (testimonialIndex !== null) {
                const videoIds = showBachelors ? bachelorVideoIds : testimonialVideoIds;
                const videoLibrary = showBachelors ? '746538' : '746540';
                const slideLabel = showBachelors ? 'Bachelor' : 'Testimonial';
                return (
                    <section className="assessment-testimonials" aria-label={`${slideLabel} slideshow`}>
                        <h1>{showBachelors ? 'Meet the bachelors' : 'Real stories, real connections'}</h1>
                        <div className="testimonial-player">
                            <iframe
                                key={testimonialIndex}
                                src={`https://player.mediadelivery.net/embed/${videoLibrary}/${videoIds[testimonialIndex]}?autoplay=false&loop=false`}
                                title={`${slideLabel} ${testimonialIndex + 1}`}
                                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                                allowFullScreen
                            />
                        </div>
                        <nav className="testimonial-controls" aria-label={`${slideLabel} navigation`}>
                            <button type="button" className="gold-cta" disabled={testimonialIndex === 0} onClick={() => setTestimonialIndex(index => Math.max(0, index - 1))}>Previous</button>
                            <span aria-live="polite">{testimonialIndex + 1} / {videoIds.length}</span>
                            <button type="button" className="gold-cta" disabled={testimonialIndex === videoIds.length - 1} onClick={() => setTestimonialIndex(index => Math.min(videoIds.length - 1, index + 1))}>Next</button>
                        </nav>
                        <button type="button" className="gold-cta" onClick={() => setTestimonialIndex(null)}>Back to choices</button>
                        <a href="/" className="gold-cta" onClick={() => trackPrivateEvent('continue_clicked')}>Continue to Site <span>→</span></a>
                    </section>
                );
            }
            return (
                <div className="assessment-complete">
                    <nav className="assessment-choice-art" aria-label="Choose your next step">
                        <img src={theme.upendo ? upendoPostPitchChoicesArtwork : postPitchChoicesArtwork} width="900" height="1600" alt="" draggable={false}
                            onLoad={event => event.currentTarget.parentElement.classList.add('choice-art-loaded')}
                            onError={event => event.currentTarget.parentElement.classList.remove('choice-art-loaded')}
                        />
                        <button type="button" className="assessment-choice-hit choice-testimonials" aria-label="Look inside the journeys — continue to see testimonials" onClick={() => { setShowBachelors(false); setTestimonialIndex(0); trackPrivateEvent('testimonials_clicked'); }}>
                            <span className="assessment-choice-label">Continue to See Testimonials →</span>
                        </button>
                        <button type="button" className="assessment-choice-hit choice-bachelors" aria-label="Meet some of our men — continue to bachelors" onClick={() => { setShowBachelors(true); setTestimonialIndex(0); trackPrivateEvent('bachelors_clicked'); }}>
                            <span className="assessment-choice-label">Continue to Bachelors →</span>
                        </button>
                        <a href="/" className="assessment-choice-hit choice-site" aria-label={`Explore ${theme.title} — continue to site`} onClick={() => trackPrivateEvent('continue_clicked')}>
                            <span className="assessment-choice-label">Continue to Site →</span>
                        </a>
                    </nav>
                </div>
            );
        }
        if (showQualifiedVideo) {
            return renderShell(
                <div className="poster-screen video-screen">
                    <div className="qualified-video-wrap">
                        <video
                            ref={pitchVideoRef}
                            src={getAssessmentQualifiedVideoUrl(hostname)}
                            aria-label={`${theme.title} qualification video`}
                            autoPlay
                            playsInline
                            controls={false}
                            controlsList="nodownload noplaybackrate nofullscreen noremoteplayback"
                            disablePictureInPicture
                            disableRemotePlayback
                            tabIndex={-1}
                            onContextMenu={(event) => event.preventDefault()}
                            onPlaying={() => { trackVideoEvent('video_started'); setShowPlayPrompt(false); }}
                            onEnded={() => {
                                trackAssessmentEvent('CompleteRegistration');
                                trackVideoEvent('video_completed');
                                setShowContinue(true);
                            }}
                            onError={() => trackVideoEvent('video_failed')}
                        />
                        {showPlayPrompt && <button type="button" className="gold-cta pitch-play" onClick={playPitchVideo}>Play video with sound</button>}
                    </div>

                </div>,
                'poster-phone',
                2
            );
        }

        return renderPosterScreen(
            qualifiedPosterImage,
            "You're about to watch a short introductory video."
        );
    };

    const renderStep = () => {
        if (!privateFlow) return renderLanding();
        if (step === -1) return renderIneligible();
        if (step === 99) return renderQualified();
        if (step === 0) return renderIntro();
        if (consolidatedFlow && step >= 1 && step <= gateSteps.length) return renderAboutYou();
        if (step >= 1 && step <= gateSteps.length) return renderGateStep(step - 1);
        const privateIndex = step - privateStartStep;
        if (privateIndex >= 0 && privateIndex < flowQuestions.length) return renderPrivateQuestion(privateIndex);
        return renderQualified();
    };

    return (
        <div ref={funnelRef} className={`AssessmentFunnel luxury-assessment${theme.upendo ? ' assessment-upendo' : ''}${consolidatedFlow ? ' halisi-assessment' : ''}${halisiDesign && (!privateFlow || (step >= 0 && step < 99)) ? ' assessment-atelier' : ''}`}>
            <style>{`
                .luxury-assessment{position:fixed;inset:0;width:100vw;height:100vh;height:100dvh;background:#071833;color:#fff;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;text-transform:none;overflow-x:hidden;overflow-y:auto;}.luxury-assessment *{text-transform:none;}
                .assessment-stage{width:100vw;min-height:100vh;min-height:100dvh;display:flex;align-items:center;justify-content:center;padding:18px;background:radial-gradient(circle at 50% 0%,rgba(46,106,164,.32),transparent 34%),linear-gradient(160deg,#061228 0%,#071b3a 46%,#020817 100%);}
                .assessment-phone{position:relative;overflow:hidden;width:min(100%,430px);min-height:min(880px,calc(100vh - 28px));border-radius:36px;border:1px solid rgba(255,255,255,.14);background-image:linear-gradient(180deg,rgba(2,8,18,.30),rgba(2,8,18,.82) 48%,rgba(2,8,18,.94)),var(--assessment-bg);background-size:cover;background-position:center;box-shadow:0 26px 90px rgba(0,0,0,.44),inset 0 0 0 1px rgba(246,207,100,.08);padding:34px 24px;display:flex;flex-direction:column;}
                .private-phone{background-image:radial-gradient(ellipse at 50% 0%,rgba(46,106,164,.32),transparent 55%),linear-gradient(160deg,#061228 0%,#071b3a 46%,#020817 100%);}
                .poster-phone{padding:0;background-image:linear-gradient(180deg,rgba(2,8,18,.12),rgba(2,8,18,.38)),var(--assessment-bg);}
                .poster-screen{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;gap:18px;min-height:100%;width:100%;overflow-y:auto;padding:0 0 22px;}
                .poster-screen.video-screen{justify-content:center;padding:22px 0;gap:24px;}
                .assessment-complete{min-height:100vh;min-height:100dvh;display:flex;align-items:center;justify-content:center;background:#f8f3e8;padding:0;}
                .assessment-choice-art{position:relative;display:block;flex:none;width:min(100%,calc((100dvh - var(--assessment-footer-height))*9/16));aspect-ratio:9/16;}.assessment-choice-art img{display:block;width:100%;height:auto;pointer-events:none;user-select:none;}.assessment-choice-hit{position:absolute;left:3%;width:94%;display:block;border:0;border-radius:7%/16%;padding:0;background:transparent;cursor:pointer;text-decoration:none;transition:box-shadow .18s ease,background .18s ease;}.choice-testimonials{top:17%;height:23.2%;}.choice-bachelors{top:43%;height:23.2%;}.choice-site{top:68%;height:14.9%;}.assessment-choice-hit:hover{background:rgba(255,234,167,.09);box-shadow:0 0 20px rgba(246,207,100,.55);}.assessment-choice-hit:focus-visible{outline:4px solid #fff;outline-offset:-6px;box-shadow:0 0 0 3px #c8871d;}.assessment-choice-art:not(.choice-art-loaded) .assessment-choice-hit{background:linear-gradient(135deg,#102647,#06152d);border:2px solid #d4ad55;color:#f6cf64;}.assessment-choice-label{display:flex;width:100%;height:100%;align-items:center;justify-content:center;padding:16px;font:700 clamp(.9rem,3vw,1.15rem)/1.4 Georgia,serif;text-align:center;}.choice-art-loaded .assessment-choice-label{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
                .assessment-testimonials{width:min(100%,720px);margin:0 auto;padding:24px 16px;display:flex;flex-direction:column;align-items:center;gap:18px;}.assessment-testimonials h1{font-family:Georgia,serif;font-size:1.6rem;text-align:center;margin:0;}.testimonial-player{width:min(100%,340px);height:min(55dvh,600px);background:#020812;border:1px solid ${gold};border-radius:16px;overflow:hidden;}.testimonial-player iframe{width:100%;height:100%;border:0;display:block;}.testimonial-controls{display:flex;align-items:center;justify-content:center;gap:14px;}.testimonial-controls .gold-cta{font-size:.9rem;padding:12px 18px;}.assessment-testimonials a{text-decoration:none;}
                .assessment-phone-input{display:flex;align-items:stretch;gap:8px;min-width:0;}.assessment-phone-input select{width:43%;min-width:0;border:1px solid rgba(255,255,255,.22);border-radius:16px;background:#041023;color:#fff;padding:12px 8px;font-size:.85rem;}.assessment-phone-input input{flex:1;min-width:0;}.assessment-phone-input select:focus{outline:none;border-color:${gold};box-shadow:0 0 0 2px rgba(246,207,100,.16);}
                .video-cta{margin-top:12px;}
                .result-poster-screen{position:fixed;inset:0;width:100vw;height:100vh;height:100dvh;display:flex;align-items:center;justify-content:center;background:#071833;overflow:hidden;z-index:1;}.result-poster{display:block;width:100%;height:100%;object-fit:contain;object-position:center;border-radius:0;}
                .qualified-video-wrap{position:relative;width:calc(100% - 28px);aspect-ratio:9/16;flex-shrink:0;border-radius:18px;overflow:hidden;border:1px solid rgba(246,207,100,.55);box-shadow:0 18px 42px rgba(0,0,0,.38);background:#020812;}
                .qualified-video-wrap video{width:100%;height:100%;border:0;display:block;background:#020812;object-fit:contain;pointer-events:none;}.pitch-play{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:85%;}.pitch-play:hover{transform:translate(-50%,-50%);}
                .gold-ribbon{position:absolute;pointer-events:none;z-index:0;width:138%;height:34%;left:-18%;border-radius:50%;border:10px solid transparent;border-top-color:rgba(255,222,122,.92);filter:drop-shadow(0 0 12px rgba(246,207,100,.45));transform:rotate(-27deg);}
                .gold-ribbon.top{top:-24%;}.gold-ribbon.bottom{bottom:-16%;}
                .gold-ribbon:after{content:"";position:absolute;inset:-15px;border-radius:inherit;border-top:3px solid rgba(255,255,255,.45);}
                .gold-orb{position:absolute;border-radius:999px;background:radial-gradient(circle,#fff6c7 0%,#f6cf64 40%,rgba(188,122,25,.18) 72%,transparent 73%);filter:blur(.2px);opacity:.42;z-index:0;}.gold-orb.one{width:160px;height:160px;right:-70px;top:22%;}.gold-orb.two{width:220px;height:220px;left:-120px;bottom:8%;}
                .intro-screen,.question-screen,.closer-screen{position:relative;z-index:1;flex:1;display:flex;flex-direction:column;}
                .intro-screen{justify-content:flex-end;gap:22px;}
                .intro-copy h1{font-family:Georgia,"Times New Roman",serif;font-size:clamp(3rem,15vw,4.75rem);line-height:.84;margin:0 0 18px;background:linear-gradient(180deg,#fff7c8,#f4c34e 55%,#b57918);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:0 8px 30px rgba(0,0,0,.2);}.intro-copy h2{font-family:Georgia,"Times New Roman",serif;font-weight:500;font-size:1.55rem;line-height:1.1;margin:0;color:#fff;}.intro-copy p,.closer-screen p,.question-subtitle{color:rgba(255,255,255,.82);font-size:1rem;line-height:1.42;margin:0 0 22px;}.gold-divider{width:56px;height:3px;border-radius:999px;background:linear-gradient(90deg,#fff5bd,#f6cf64,#a97116);margin:16px 0;}.gold-divider.centered{margin:18px auto;}
                .gold-cta{border:0;border-radius:999px;background:linear-gradient(135deg,#fff1a8 0%,#f6cf64 45%,#c8871e 100%);color:#1b1308;padding:16px 28px;font-weight:900;font-size:1.03rem;box-shadow:0 16px 38px rgba(246,207,100,.22),inset 0 1px 0 rgba(255,255,255,.55);display:inline-flex;justify-content:center;align-items:center;gap:12px;cursor:pointer;transition:transform .18s ease,box-shadow .18s ease;}.gold-cta:hover{transform:translateY(-2px);box-shadow:0 20px 44px rgba(246,207,100,.3);}.gold-cta.full{width:100%;}.gold-cta:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none;}
                .back-button{position:absolute;top:32px;left:0;border:0;background:transparent;color:#fff;font-size:42px;line-height:1;cursor:pointer;z-index:2;}.step-label{text-align:center;margin:22px 0 8px;font-family:Georgia,"Times New Roman",serif;font-size:1rem;color:rgba(255,255,255,.9);}.assessment-progress{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin:0 auto 54px;width:min(190px,72%);}.assessment-progress span{height:10px;border-radius:999px;background:rgba(255,255,255,.28);box-shadow:inset 0 0 10px rgba(255,255,255,.1);}.assessment-progress span.is-active{background:linear-gradient(90deg,#fff1a8,#f6cf64,#c8871e);box-shadow:0 0 15px rgba(246,207,100,.48);}
                .question-screen{padding-top:16px;}.question-title{font-family:Georgia,"Times New Roman",serif;font-weight:500;text-align:center;font-size:clamp(2rem,9vw,2.75rem);line-height:1.03;margin:0 0 42px;color:#fff;}.question-title.smaller{font-size:clamp(1.45rem,6.5vw,2rem);line-height:1.13;margin-bottom:18px;}.question-title span{color:${gold};}.option-list{display:flex;flex-direction:column;gap:13px;margin-top:auto;margin-bottom:auto;}.option-list.compact{gap:10px;margin-top:8px;}.lux-option{width:100%;min-height:90px;border:1px solid rgba(255,255,255,.26);border-radius:16px;background:linear-gradient(145deg,rgba(12,39,78,.76),rgba(4,16,35,.74));color:#fff;text-align:left;padding:14px 16px 14px 12px;display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:16px;font-family:Georgia,"Times New Roman",serif;font-size:1.18rem;line-height:1.16;cursor:pointer;box-shadow:inset 0 0 0 1px rgba(255,255,255,.02);transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease;}.lux-option:hover,.lux-option.selected{border-color:${gold};box-shadow:0 0 0 1px rgba(246,207,100,.45),0 0 28px rgba(246,207,100,.16);transform:translateY(-1px);}.lux-option b{font-size:32px;font-weight:300;color:rgba(255,255,255,.84);}.option-medallion{width:60px;height:60px;border-radius:50%;display:grid;place-items:center;background:linear-gradient(135deg,#fff3ac,#f6cf64 52%,#bf7c18);color:#071832;font-family:Georgia,"Times New Roman",serif;font-size:1.2rem;font-weight:900;box-shadow:inset 0 1px 0 rgba(255,255,255,.5);}.option-medallion.small{width:44px;height:44px;font-size:.85rem;}.text-option{font-family:Inter,system-ui,sans-serif;font-size:.88rem;min-height:72px;line-height:1.26;}
                .gate-content{display:flex;flex-direction:column;flex:1;}.gate-gender .gate-content,.gate-age .gate-content{flex:0;margin-top:auto;}.gate-gender .option-list,.gate-age .option-list{margin:0;}.gate-gender .question-title,.gate-age .question-title{margin-bottom:20px;}.gate-age .question-title{font-size:clamp(1.55rem,7vw,1.85rem);}.gate-age .option-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}.gate-age .lux-option{min-height:60px;padding:8px;font-size:1rem;}.gate-age .lux-option b{display:none;}.town-picker{display:flex;flex-direction:column;gap:10px;margin-top:16px;}.town-picker label{color:#f6cf64;font-size:14px;}.town-picker input,.town-picker select{box-sizing:border-box;width:100%;min-width:0;min-height:48px;padding:12px;border:1px solid #9c813f;border-radius:12px;background:#122b40;color:#fff;font-size:16px;}.town-picker select option{background:#122b40;color:#fff;}.town-picker p{font-size:14px;color:#fff;}.gate-age .option-medallion{width:44px;height:44px;font-size:1rem;}.gate-gender .assessment-progress,.gate-age .assessment-progress{margin-bottom:24px;}.phone-help{font-size:.8rem;color:rgba(255,255,255,.8);margin:0;}
                .private-screen{padding-top:52px;}.private-screen .back-button{top:0;}.private-screen .question-title.smaller{font-size:clamp(.9rem,3.5vw,1rem);line-height:1.4;text-align:left;margin-bottom:14px;}.private-screen .financial-paragraph{display:block;color:inherit;}.financial-paragraph + .financial-paragraph{margin-top:6px;}.private-screen .step-label{color:${gold};font-weight:800;text-transform:uppercase;letter-spacing:.12em;font-family:Inter,system-ui,sans-serif;font-size:.74rem;}.closer-screen{text-align:center;justify-content:space-between;padding:34px 0 8px;}.closer-screen h1{font-family:Georgia,"Times New Roman",serif;font-size:2rem;line-height:1.14;margin:0;color:#fff;}.closer-screen footer{font-size:.68rem;color:rgba(255,255,255,.7);line-height:1.6;margin-top:18px;}.closer-screen footer span{color:${gold};margin:0 6px;}.contact-fields{display:flex;flex-direction:column;gap:13px;margin-top:14px;}.contact-fields input{width:100%;border:1px solid rgba(255,255,255,.22);border-radius:16px;background:rgba(4,16,35,.74);color:#fff;padding:16px;font-size:1rem;outline:none;}.contact-fields input::placeholder{color:rgba(255,255,255,.58);}.contact-fields input:focus{border-color:${gold};box-shadow:0 0 0 2px rgba(246,207,100,.16);}
                .bridge-screen{justify-content:center;gap:24px;}.bridge-copy .gold-divider{margin:0 auto 14px;}.bridge-copy h1{font-size:clamp(1.1rem,4.5vw,1.35rem);line-height:1.3;}.bridge-copy p{font-size:.85rem;line-height:1.45;margin:10px 0 0;}
                @media (max-width:460px){.assessment-stage{padding:0;background:#071832;}.assessment-phone{width:100%;min-height:100vh;border-radius:0;border:0;padding:36px 22px 28px;box-shadow:none;}.question-title{margin-bottom:34px}.lux-option{min-height:78px;font-size:1.05rem}.intro-copy h1{font-size:4.1rem}.poster-phone{padding:0}.result-poster{border-radius:0}.qualified-video-wrap{width:calc(100% - 24px)}}
                .luxury-assessment{--assessment-footer-height:84px;padding-bottom:var(--assessment-footer-height);}
                .assessment-stage{min-height:calc(100vh - var(--assessment-footer-height));min-height:calc(100dvh - var(--assessment-footer-height));}
                .assessment-phone{min-height:min(880px,calc(100dvh - var(--assessment-footer-height) - 36px));}
                .result-poster-screen{bottom:var(--assessment-footer-height);height:calc(100dvh - var(--assessment-footer-height));}
                .upendo-result-poster-screen{background:#090709;}
                .assessment-complete{min-height:calc(100dvh - var(--assessment-footer-height));}
                .assessment-footer{position:fixed;bottom:0;left:0;right:0;z-index:10;height:var(--assessment-footer-height);padding:14px 12px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;text-align:center;color:rgba(255,255,255,.8);background:#061228;border-top:1px solid rgba(246,207,100,.18);font-size:.75rem;line-height:1.4;}
                .assessment-footer a{color:inherit;text-decoration:underline;text-underline-offset:3px;}.assessment-footer p{margin:0;}.assessment-footer span{margin:0 5px;color:${gold};}
                .landing-screen .bridge-copy h1{font-size:clamp(1.5rem,6vw,1.9rem);line-height:1.22;}.landing-screen .bridge-copy p{font-size:.9rem;line-height:1.6;margin-top:20px;}.landing-screen .gold-cta{text-decoration:none;}
                @media(max-width:460px){.assessment-phone{min-height:calc(100dvh - var(--assessment-footer-height));}}
                .intro-phone .intro-copy h1{font-size:clamp(1.85rem,7vw,2.35rem);line-height:1.12;margin-bottom:12px;}
                .intro-phone .intro-copy h2{font-size:clamp(.95rem,3.6vw,1.1rem);line-height:1.45;}
                .luxury-assessment,.luxury-assessment *{box-sizing:border-box;}
                .assessment-upendo,.assessment-upendo .assessment-stage{background:#120d10;}
                .assessment-upendo *{box-sizing:border-box;}
                .assessment-upendo .assessment-stage{padding-top:92px;background:radial-gradient(ellipse at top,#482032,transparent 65%),#120d10;}
                .assessment-upendo .assessment-phone{border-color:#b28b49;background-image:linear-gradient(180deg,rgba(18,13,16,.5),rgba(18,13,16,.94)),var(--assessment-bg);}
                .assessment-upendo .private-phone{background:linear-gradient(155deg,#301922,#120d10);}
                .assessment-upendo .assessment-brand{position:absolute;top:0;left:0;right:0;height:78px;z-index:3;display:flex;align-items:center;justify-content:center;background:#120d10;border-bottom:1px solid #b28b49;}
                .assessment-upendo .assessment-brand-logo{display:block;width:116px;height:58px;object-fit:contain;border-radius:8px;}
                .assessment-upendo .gold-cta{background:linear-gradient(135deg,#f6cf64,#eea3b6);color:#201017;box-shadow:0 12px 32px #ec91ae26;}
                .assessment-upendo .lux-option{background:linear-gradient(145deg,#3a202e,#1d1218);}
                .assessment-upendo .lux-option:hover,.assessment-upendo .lux-option.selected{border-color:#eea3b6;box-shadow:0 0 0 1px #eea3b6;}
                .assessment-upendo .contact-fields input,.assessment-upendo .assessment-phone-input select,.assessment-upendo .town-picker input{background:#21151b;}
                .assessment-upendo .intro-copy h1,.assessment-upendo .question-title,.assessment-upendo .step-label{font-family:Inter,system-ui,sans-serif;}
                .assessment-upendo .gold-orb{opacity:.16;}
                .assessment-upendo .assessment-footer{background:#120d10;border-color:#b28b49;}
                .assessment-upendo .upendo-result{flex-direction:column;gap:24px;padding:100px 24px 30px;text-align:center;background:radial-gradient(ellipse at top,#482032,transparent 70%),#120d10;}
                .upendo-result h1{color:#f6cf64;font-size:clamp(1.6rem,7vw,2.5rem);line-height:1.2;max-width:540px;}
                .upendo-result p{color:#fff;line-height:1.6;max-width:480px;}
                .assessment-upendo .assessment-complete{padding:0;background:#120d10;}
                .assessment-upendo .assessment-choice-art:not(.choice-art-loaded) .assessment-choice-hit{background:linear-gradient(135deg,#8c0043,#090507);border-color:#d4ad55;}
                .assessment-upendo .assessment-testimonials{padding-top:100px;}
                .assessment-stages{position:relative;margin-bottom:22px;}.assessment-stages .step-label{margin:0 22px 14px;color:${gold};font-family:inherit;font-size:.85rem;letter-spacing:0;text-transform:none;}.assessment-stages ol{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;list-style:none;padding:0;margin:0;}.assessment-stages li{text-align:center;font-size:.7rem;line-height:1.3;color:rgba(255,255,255,.65);border-top:4px solid rgba(255,255,255,.25);padding-top:8px;}.assessment-stages li.is-active{border-color:${gold};color:#fff;}.assessment-stages li[aria-current=step]{font-weight:800;}.assessment-stages li span{display:block;font-size:.85rem;margin-bottom:3px;}.about-you-screen{padding-top:48px;}.about-you-screen .back-button{top:0;}.about-you-screen .question-title{margin-bottom:10px;}.about-you-fields{margin-top:0;gap:9px;}.about-you-fields .gold-cta{margin-top:14px;}.about-you-fields label{margin:8px 0 0;}.intro-screen:has(.assessment-stages){justify-content:flex-start;}.intro-screen:has(.assessment-stages) .intro-copy{margin-top:auto;}
                /* Assessment atelier: deliberately scoped away from outcomes and other hosts.
                   Avoid legacy lux-option/assessment-phone palette overrides from site-theme. */
                .assessment-atelier{--atelier-bg:#050e20;--atelier-panel:#0b1930;--atelier-gold:#dfbd77;--atelier-ivory:#faf3e6;--atelier-muted:#c1c4cb;--atelier-accent:#8d354d;background:var(--atelier-bg);color:var(--atelier-ivory);}
                .assessment-atelier.assessment-upendo{--atelier-bg:#0b070a;--atelier-panel:#1c1119;--atelier-gold:#e4bf70;--atelier-ivory:#fff7f1;--atelier-muted:#d2bec9;--atelier-accent:#ec0073;}
                .assessment-atelier .assessment-brand{position:relative;inset:auto;height:88px;max-width:1120px;margin:auto;padding:12px 28px;display:flex;justify-content:space-between;align-items:center;border:0;border-bottom:1px solid #dfbd7738;background:transparent;}
                .assessment-atelier .assessment-brand-logo{width:72px;height:62px;object-fit:contain;border-radius:6px;}
                .atelier-header-caption{font-size:10px;letter-spacing:.24em;color:var(--atelier-gold);}
                .assessment-atelier .assessment-stage{width:100%;min-height:calc(100dvh - 172px);padding:32px 24px 44px;align-items:flex-start;background:radial-gradient(ellipse at 85% 10%,color-mix(in srgb,var(--atelier-accent) 14%,transparent),transparent 60%),var(--atelier-bg)!important;}
                .assessment-atelier .assessment-phone{width:min(100%,680px);min-height:0;overflow:hidden;padding:30px 40px 36px;border:1px solid #dfbd7770;border-radius:22px;box-shadow:0 24px 80px #0005;}
                .luxury-assessment.assessment-atelier .assessment-phone[class]{background:linear-gradient(145deg,var(--atelier-panel),var(--atelier-bg))!important;}
                .assessment-atelier .assessment-phone.atelier-panel{width:min(100%,1064px);padding:0;}
                .assessment-atelier .gold-orb{display:none;}
                .assessment-atelier .gold-ribbon{width:460px;height:220px;left:auto;right:-290px;border-width:3px;filter:none;opacity:.6;}
                .assessment-atelier .gold-ribbon.top{top:-180px;}.assessment-atelier .gold-ribbon.bottom{bottom:-200px;left:-120px;}
                .assessment-atelier .gold-ribbon:after{border-top:12px solid var(--atelier-accent);inset:-18px;opacity:.6;}
                .atelier-landing{display:flex;align-items:center;min-height:570px;background:linear-gradient(90deg,rgba(5,14,32,.96) 0%,rgba(5,14,32,.86) 45%,rgba(5,14,32,.2) 100%),url('/assets/img/assessment/assessment-connection.webp') center 42%/cover;}
                .assessment-upendo .atelier-landing{background-image:linear-gradient(90deg,rgba(11,7,10,.96) 0%,rgba(28,9,20,.86) 45%,rgba(11,7,10,.2) 100%),url('/assets/img/assessment/assessment-connection.webp');}
                .atelier-landing-copy{position:relative;padding:54px 42px;width:56%;min-width:0;}
                .atelier-eyebrow{font:600 10px/1.6 system-ui,sans-serif;letter-spacing:.24em;color:var(--atelier-gold);margin:0 0 24px;}
                .assessment-atelier h1,.assessment-atelier .intro-copy h1,.assessment-atelier .question-title.smaller{font:400 clamp(32px,3.6vw,49px)/1.12 Georgia,'Times New Roman',serif;letter-spacing:-.025em;color:var(--atelier-ivory);background:none;text-shadow:none;margin:0 0 18px;text-align:left;}
                .assessment-atelier h1 em{font-style:normal;color:var(--atelier-gold);}
                .assessment-atelier .atelier-description{font:400 14px/1.75 system-ui,sans-serif;color:var(--atelier-muted);margin:0 0 24px;}
                .atelier-journey{display:flex;flex-wrap:wrap;gap:10px 18px;padding:20px 0;margin:26px 0;border-block:1px solid #dfbd7733;color:var(--atelier-gold);font:500 11px/1.5 system-ui,sans-serif;}
                .assessment-atelier .gold-cta{min-height:50px;border:1px solid var(--atelier-gold);border-radius:8px;padding:14px 26px;background:linear-gradient(115deg,#f4dfa7,#d0a856);color:#201808;font:650 14px/1.4 system-ui,sans-serif;box-shadow:none;text-decoration:none;}
                .assessment-atelier.assessment-upendo .gold-cta{background:linear-gradient(115deg,#b90059,#ec0073);color:#fff7f1;border-color:#ff61a9;}
                .assessment-atelier .gold-cta:disabled{opacity:.5;}
                .atelier-note{font:400 11px/1.6 system-ui,sans-serif;color:var(--atelier-muted);margin:16px 0 0;}

                .assessment-atelier .intro-screen,.assessment-atelier .question-screen{padding-top:0;display:block;}
                .assessment-atelier .assessment-stages{margin:0 0 30px;padding:0;}
                .assessment-atelier .assessment-stages .step-label{font:600 10px/1.6 system-ui,sans-serif;letter-spacing:.12em;text-transform:uppercase;color:var(--atelier-gold);margin:0 24px 18px;}
                .assessment-atelier .assessment-stages ol{gap:12px;}
                .assessment-atelier .assessment-stages li{font:400 10px/1.4 system-ui,sans-serif;border-top:2px solid #dfbd7738;padding-top:10px;color:var(--atelier-muted);}
                .assessment-atelier .assessment-stages li.is-active{border-color:var(--atelier-gold);color:var(--atelier-ivory);}
                .assessment-atelier .assessment-stages li[aria-current=step]{color:var(--atelier-gold);}
                .assessment-atelier .assessment-stages li span{display:inline;font-size:10px;margin-right:3px;}
                .assessment-atelier .back-button{top:-7px;left:-12px;width:32px;height:32px;display:grid;place-items:center;border:1px solid #dfbd7770;border-radius:50%;font-size:28px;line-height:1;color:var(--atelier-gold);}
                .assessment-atelier .intro-copy{margin-top:0!important;}
                .assessment-atelier .intro-copy h1{max-width:450px;font-size:37px;}
                .assessment-atelier .intro-copy h2{font:500 15px/1.5 system-ui,sans-serif;color:var(--atelier-gold);margin:0 0 6px;}
                .assessment-atelier .gold-divider{display:none;}
                .assessment-atelier .contact-fields{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;margin:26px 0 24px;}
                .assessment-atelier .contact-fields label{font:500 12px/1.5 system-ui,sans-serif;color:var(--atelier-gold);}
                .assessment-atelier .contact-fields label[for=assessment-first-name]{grid-column:1;grid-row:1;}
                .assessment-atelier .contact-fields label[for=assessment-last-name]{grid-column:2;grid-row:1;}
                .assessment-atelier #assessment-first-name{grid-column:1;grid-row:2;}
                .assessment-atelier #assessment-last-name{grid-column:2;grid-row:2;}
                .assessment-atelier .contact-fields>:nth-child(n+5){grid-column:1/-1;}
                .assessment-atelier .contact-fields input,.assessment-atelier .assessment-phone-input select,.assessment-atelier .town-picker input,.assessment-atelier .town-picker select{border:1px solid #dfbd7760;border-radius:7px;background:var(--atelier-bg);padding:13px 12px;min-height:48px;color:var(--atelier-ivory);font:400 16px/1.4 system-ui,sans-serif;}
                .assessment-atelier .contact-fields input::placeholder{color:#a7a4a7;}
                .assessment-atelier .phone-help{font:400 11px/1.6 system-ui,sans-serif;color:var(--atelier-muted);margin:0 0 10px;}
                .assessment-atelier .intro-copy>.gold-cta,.assessment-atelier .about-you-fields .gold-cta{width:100%;}
                .assessment-atelier .about-you-fields{gap:9px;margin-top:20px;}
                .assessment-atelier .about-you-fields label{font:500 12px/1.5 system-ui,sans-serif;color:var(--atelier-gold);margin:8px 0 0;}
                .atelier-price{padding:18px 20px;border:1px solid #dfbd7760;border-radius:10px;margin:22px 0;background:linear-gradient(110deg,#dfbd7710,transparent);}
                .atelier-price span{display:block;font:600 9px/1.6 system-ui,sans-serif;letter-spacing:.2em;color:var(--atelier-muted);margin-bottom:8px;}
                .atelier-price strong{display:block;font:400 clamp(20px,3vw,27px)/1.3 Georgia,serif;color:var(--atelier-gold);}
                .assessment-atelier .option-list.compact{gap:12px;margin:24px 0 0;}
                .assessment-atelier .lux-option.text-option{position:relative;min-height:96px;border:1px solid #dfbd77a0!important;border-radius:12px;padding:18px;gap:16px;font:400 14px/1.55 system-ui,sans-serif;background:linear-gradient(120deg,var(--atelier-panel),var(--atelier-bg))!important;}
                .assessment-atelier .lux-option.text-option:first-child{background:linear-gradient(90deg,var(--atelier-panel) 20%,color-mix(in srgb,var(--atelier-bg) 89%,transparent)),url('/assets/img/assessment/assessment-connection.webp') center 40%/cover!important;}
                .assessment-atelier .lux-option.text-option:hover{border-color:#fff0c1!important;box-shadow:0 0 0 1px var(--atelier-gold);}
                .assessment-atelier .lux-option.text-option[aria-pressed="true"]{border-color:#fff0c1!important;box-shadow:0 0 0 2px var(--atelier-gold);}
                .assessment-atelier .option-medallion.small{width:40px;height:40px;font:400 25px/1 Georgia,serif;background:linear-gradient(135deg,#fff0c1,#c69a43);color:#151322;}
                .assessment-atelier .lux-option b{font-size:26px;color:var(--atelier-gold);}
                .assessment-atelier :is(button,a,input,select):focus-visible{outline:2px solid var(--atelier-gold);outline-offset:4px;}
                .assessment-atelier .assessment-footer{background:var(--atelier-bg)!important;border-color:#dfbd7738;color:var(--atelier-muted);font-size:11px;}
                @media(max-width:600px){
                  .assessment-atelier .assessment-brand{height:76px;padding:10px 20px;}.assessment-atelier .assessment-brand-logo{width:62px;height:54px;}.atelier-header-caption{font-size:8px;letter-spacing:.16em;}
                  .assessment-atelier .assessment-stage{padding:20px 14px 28px;min-height:calc(100dvh - 160px);}.assessment-atelier .assessment-phone{padding:24px 20px 28px;border-radius:16px;min-height:0;}
                  .atelier-landing{min-height:0;background-image:linear-gradient(180deg,rgba(5,14,32,.7),rgba(5,14,32,.88)),url('/assets/img/assessment/assessment-connection.webp');background-position:center 40%;}.assessment-upendo .atelier-landing{background-image:linear-gradient(180deg,rgba(11,7,10,.7),rgba(28,9,20,.88)),url('/assets/img/assessment/assessment-connection.webp');}.atelier-landing-copy{padding:30px 24px;width:100%;}.atelier-landing h1{font-size:38px;}.atelier-eyebrow{margin-bottom:18px;}.atelier-journey{margin:20px 0;padding:16px 0;gap:8px 14px;font-size:10px;}.atelier-landing .gold-cta{width:100%;}
                  .assessment-atelier .intro-copy h1,.assessment-atelier h1,.assessment-atelier .question-title.smaller{font-size:31px;}.assessment-atelier .assessment-stages{margin-bottom:26px;}.assessment-atelier .assessment-stages li{font-size:9px;}.assessment-atelier .assessment-stages ol{gap:7px;}.assessment-atelier .assessment-stages .step-label{font-size:9px;letter-spacing:.05em;}
                  .assessment-atelier .atelier-description{font-size:13px;line-height:1.7;margin-bottom:18px;}.assessment-atelier .assessment-phone-input select{width:43%;font-size:12px;padding:12px 5px;}.assessment-atelier .assessment-phone-input input{width:0;flex:1;font-size:14px;padding:12px 8px;}
                  .assessment-atelier .lux-option.text-option{padding:16px 12px;gap:12px;font-size:13px;}.assessment-atelier .option-medallion.small{width:32px;height:32px;font-size:22px;}.atelier-price{padding:16px;}.atelier-price strong{font-size:22px;}
                }
                @media(prefers-reduced-motion:reduce){.assessment-atelier *{transition:none!important;scroll-behavior:auto!important;}}
            `}</style>
            {((theme.upendo && !(showContinue && testimonialIndex === null)) || (halisiDesign && (!privateFlow || (step >= 0 && step < 99)))) && <header className="assessment-brand"><img className="assessment-brand-logo" src={theme.upendo ? theme.logo : '/assets/img/phlogo.jpeg'} alt={theme.title} />{halisiDesign && (!privateFlow || (step >= 0 && step < 99)) && <span className="atelier-header-caption">PRIVATE MATCH DISCOVERY</span>}</header>}
            {renderStep()}
            <footer className="assessment-footer">
                <nav aria-label="Assessment footer">
                    <a href="/privacypolicy">Privacy Policy</a><span aria-hidden="true"> | </span>
                    <a href="/termssandconditions">Terms of Service</a><span aria-hidden="true"> | </span>
                    <a href="https://wa.me/447456722140" target="_blank" rel="noopener noreferrer">Contact Us</a>
                </nav>
                <p>{`© 2017 ${theme.title}. All rights reserved.`}</p>
            </footer>
        </div>
    );
};

export default AssessmentFunnel;
